import React from 'react'

import queryString from 'query-string'

import { ZariContext } from '_zari/context'

import { Button, Col, Row, Portlet, Table } from '_zari/core'

import { CodigosTransaccionesConstant, OrigenesContabilidadConstant } from 'constants/index'

import * as AsientoContableService from 'services/asientosContables.service'

import * as FormGroups from './formGroups'
import * as Modals from './modals'

import { DownloadAxiosFile } from 'utils'

const portletBreadcrumbs = [{ name: 'Asientos Contables' }]

const ListaSociosPage = ({ history, location }) => {
  const __zariContext = React.useContext(ZariContext)

  const tablaRef = React.useRef(null)

  const initialQueryParams = React.useMemo(() => {
    if (location.search) {
      const parsed = queryString.parse(location.search)
      return parsed
    } else {
      return {}
    }
  }, [location.search])

  const preventFirstRender = React.useMemo(() => {
    return !initialQueryParams.origen
  }, [initialQueryParams])

  const [idOrigen, setOrigen] = React.useState(initialQueryParams.origen || '')
  const [mostrarClienteProveedorModal, setMostrarClienteProveedorModal] = React.useState(false)
  const [mostrarFormatosComprasModal, setMostrarFormatosComprasModal] = React.useState(false)
  const [mostrarFormatosDiariosModal, setMostrarFormatosDiariosModal] = React.useState(false)

  const clickVerDetalles = React.useCallback(
    async ({ _id }) => {
      history.push(`/${CodigosTransaccionesConstant.ASIENTOSCONTABLES_DETALLES}/${_id}`)
    },
    [history]
  )

  const clickEditar = React.useCallback(
    async ({ _id }) => {
      if (!idOrigen) {
        __zariContext.snack('warning', 'Seleccionar origen.')
        return
      }

      const queryParams = queryString.stringify({
        origen: idOrigen
      })

      if (idOrigen === OrigenesContabilidadConstant.COMPRAS || idOrigen === OrigenesContabilidadConstant.VENTAS) {
        history.push(`/${CodigosTransaccionesConstant.ASIENTOSCONTABLES_NUEVO}/${_id}?${queryParams}`)
      } else {
        history.push(`/${CodigosTransaccionesConstant.ASIENTOSCONTABLESORIGENDIARIO_NUEVO}/${_id}?${queryParams}`)
      }
    },
    [__zariContext, history, idOrigen]
  )

  const clickBuscarHandler = React.useCallback(() => {
    if (!idOrigen) {
      __zariContext.snack('warning', 'Seleccionar origen.')
      return
    }

    const queryParams = queryString.stringify({
      origen: idOrigen
    })

    history.push(`/${CodigosTransaccionesConstant.ASIENTOSCONTABLES_LISTA}?${queryParams}`)

    tablaRef.current.reload({
      origen: idOrigen
    })
  }, [idOrigen, history, __zariContext])

  const clickNuevo = React.useCallback(() => {
    if (!idOrigen) {
      __zariContext.snack('warning', 'Seleccionar origen.')
      return
    }

    const queryParams = queryString.stringify({
      origen: idOrigen
    })

    if (idOrigen === OrigenesContabilidadConstant.COMPRAS || idOrigen === OrigenesContabilidadConstant.VENTAS) {
      history.push(`/${CodigosTransaccionesConstant.ASIENTOSCONTABLES_NUEVO}?${queryParams}`)
    } else if (idOrigen === OrigenesContabilidadConstant.CANJE_LETRAS) {
      setMostrarClienteProveedorModal(true)
    } else if (
      idOrigen === OrigenesContabilidadConstant.COBRANZAS ||
      idOrigen === OrigenesContabilidadConstant.CAJA_EGRESOS ||
      idOrigen === OrigenesContabilidadConstant.BANCOS
    ) {
      history.push(`/${CodigosTransaccionesConstant.ASIENTOSCONTABLES_NUEVOOGCOBR}?${queryParams}`)
    } else {
      history.push(`/${CodigosTransaccionesConstant.ASIENTOSCONTABLESORIGENDIARIO_NUEVO}?${queryParams}`)
    }
  }, [history, idOrigen, __zariContext])

  const clickNuevoSinPlantilla = React.useCallback(() => {
    if (!idOrigen) {
      __zariContext.snack('warning', 'Seleccionar origen.')
      return
    }

    const queryParams = queryString.stringify({
      origen: idOrigen
    })

    history.push(`/${CodigosTransaccionesConstant.ASIENTOSCONTABLESORIGENDIARIO_NUEVO}?${queryParams}`)
  }, [history, idOrigen, __zariContext])

  const clickNuevoConFormato = React.useCallback(() => {
    if (!idOrigen) {
      __zariContext.snack('warning', 'Seleccionar origen.')
      return
    }

    if (idOrigen === OrigenesContabilidadConstant.COMPRAS) {
      setMostrarFormatosComprasModal(true)
    } else if (idOrigen === OrigenesContabilidadConstant.DIARIO) {
      setMostrarFormatosDiariosModal(true)
    }
  }, [__zariContext, idOrigen])

  const clickImprimirTicket = React.useCallback(
    ({ _id }) => {
      AsientoContableService.imprimirPdfTicketCobranzas(_id)
        .then(({ data, headers }) => {
          DownloadAxiosFile.downloadAsBlob(data, headers)
        })
        .catch(({ data: { error } }) => __zariContext.snack('error', error))
    },
    [__zariContext]
  )

  const clickImprimirReporte = React.useCallback(
    ({ _id }) => {
      AsientoContableService.imprimirReporteAsientoContable(_id)
        .then(({ data, headers }) => {
          DownloadAxiosFile.downloadAsBlob(data, headers)
        })
        .catch(({ data: { error } }) => __zariContext.snack('error', error))
    },
    [__zariContext]
  )

  const headerButtons = React.useMemo(() => {
    if (
      idOrigen === OrigenesContabilidadConstant.VENTAS ||
      idOrigen === OrigenesContabilidadConstant.BANCOS ||
      idOrigen === OrigenesContabilidadConstant.CAJA_EGRESOS ||
      idOrigen === OrigenesContabilidadConstant.COBRANZAS ||
      idOrigen === OrigenesContabilidadConstant.CANJE_LETRAS
    ) {
      return [
        { name: 'Nuevo', onClick: clickNuevo },
        { name: 'Nuevo Sin-Plantilla', onClick: clickNuevoSinPlantilla }
      ]
    } else if (idOrigen === OrigenesContabilidadConstant.COMPRAS) {
      return [
        { name: 'Nuevo', onClick: clickNuevo },
        { name: 'Nuevo con Formato', onClick: clickNuevoConFormato }
      ]
    } else if (idOrigen === OrigenesContabilidadConstant.DIARIO) {
      return [
        { name: 'Nuevo', onClick: clickNuevo },
        { name: 'Nuevo con Formato', onClick: clickNuevoConFormato }
      ]
    }
    return [{ name: 'Nuevo', onClick: clickNuevo }]
  }, [idOrigen, clickNuevo, clickNuevoSinPlantilla, clickNuevoConFormato])

  return (
    <>
      <Portlet>
        <Portlet.Header title='Asientos Contables' breadcrumbs={portletBreadcrumbs} buttons={headerButtons} />
        <Portlet.Body>
          <Row className='zari-slim-form mb-3'>
            <Col xs={9}>
              <FormGroups.OrigenFormGroup
                value={idOrigen}
                onChange={e => {
                  setOrigen(e.target.value)
                  tablaRef.current.clear()
                }}
              />
            </Col>
            <Col xs={3}>
              <Button onClick={clickBuscarHandler} block>
                Buscar
              </Button>
            </Col>
          </Row>
          <Table.ServerSide
            useNest={true}
            useFilters={true}
            ref={tablaRef}
            baseParams={{
              origen: idOrigen
            }}
            url='/asientos-contables'
            preventFirstRender={preventFirstRender}
            headRows={[
              { label: 'ORIGEN', align: 'center' },
              {
                label: 'ANOMES',
                width: 70,
                align: 'center'
              },
              {
                label: 'NUM OPE',
                width: 80,
                align: 'center',
                filter: {
                  type: 'text',
                  name: 'numeroOperacion'
                }
              },
              { label: 'GLOSA', align: 'center' },
              {
                label: 'RUC',
                width: 110,
                align: 'center',
                filter: {
                  type: 'text',
                  name: 'ruc'
                }
              },
              {
                label: 'RAZON SOCIAL',
                align: 'center',
                filter: {
                  type: 'text',
                  name: 'razonSocial'
                }
              },
              { label: 'TIPO DOC.', align: 'center' },
              {
                label: 'SERIE',
                width: 70,
                align: 'center',
                filter: {
                  type: 'text',
                  name: 'serieDocumento'
                }
              },
              {
                label: 'NUMERO',
                width: 100,
                align: 'center',
                filter: {
                  type: 'text',
                  name: 'numeroDocumento'
                }
              },
              { label: '' }
            ]}
            rowTemplate={row => (
              <Table.Row hover key={row._id}>
                <Table.Cell align='center'>{row.origen || '--'}</Table.Cell>
                <Table.Cell align='center'>{row.anoMes || '--'}</Table.Cell>
                <Table.Cell align='center'>{row.numeroOperacion || '--'}</Table.Cell>
                <Table.Cell>{row.glosa || '--'}</Table.Cell>
                <Table.Cell align='center'>{row.numeroDocumentoIdenficacionSocio || '--'}</Table.Cell>
                <Table.Cell>{row.razonSocialSocio || '--'}</Table.Cell>
                <Table.Cell align='center'>{row.tipoDocumento || '--'}</Table.Cell>
                <Table.Cell align='center'>{row.serieDocumento || '--'}</Table.Cell>
                <Table.Cell align='center'>{row.numeroDocumento || '--'}</Table.Cell>
                <Table.Cell>
                  {row.creacionManual ? (
                    <Table.Dropdown>
                      {({ handleClose }) => [
                        <Table.Dropdown.Item
                          key='editar'
                          handleClose={handleClose}
                          onClick={e => clickEditar({ _id: row._id })}
                        >
                          Editar
                        </Table.Dropdown.Item>,
                        <Table.Dropdown.Item
                          key='verDetalles'
                          handleClose={handleClose}
                          onClick={e => clickVerDetalles({ _id: row._id })}
                        >
                          Ver Detalles / Eliminar
                        </Table.Dropdown.Item>,
                        <Table.Dropdown.Item
                          key='imprimirReporte'
                          handleClose={handleClose}
                          onClick={e => clickImprimirReporte({ _id: row._id })}
                        >
                          Imprimir
                        </Table.Dropdown.Item>,
                        <Table.Dropdown.Item
                          key='imprimirTicket'
                          handleClose={handleClose}
                          onClick={e => clickImprimirTicket({ _id: row._id })}
                        >
                          Imprimir Ticket
                        </Table.Dropdown.Item>
                      ]}
                    </Table.Dropdown>
                  ) : (
                    <Table.Dropdown>
                      {({ handleClose }) => [
                        <Table.Dropdown.Item
                          key='verDetalles'
                          handleClose={handleClose}
                          onClick={e => clickVerDetalles({ _id: row._id })}
                        >
                          Ver Detalles / Eliminar
                        </Table.Dropdown.Item>,
                        <Table.Dropdown.Item
                          key='imprimirTicket'
                          handleClose={handleClose}
                          onClick={e => clickImprimirTicket({ _id: row._id })}
                        >
                          Imprimir Ticket
                        </Table.Dropdown.Item>
                      ]}
                    </Table.Dropdown>
                  )}
                </Table.Cell>
              </Table.Row>
            )}
          />
        </Portlet.Body>
      </Portlet>
      {mostrarClienteProveedorModal && (
        <Modals.ClienteProveedorModal
          show={mostrarClienteProveedorModal}
          handleClose={() => setMostrarClienteProveedorModal(false)}
          origen={idOrigen}
        />
      )}
      {mostrarFormatosComprasModal && (
        <Modals.FormatosComprasModal
          show={mostrarFormatosComprasModal}
          handleClose={() => setMostrarFormatosComprasModal(false)}
          origen={idOrigen}
        />
      )}
      {mostrarFormatosDiariosModal && (
        <Modals.FormatosDiariosModal
          show={mostrarFormatosDiariosModal}
          handleClose={() => setMostrarFormatosDiariosModal(false)}
          origen={idOrigen}
        />
      )}
    </>
  )
}

export default ListaSociosPage
